import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ArrowLeft, Car, CalendarCheck, DoorOpen, History, IdCard, QrCode, UserCheck } from "lucide-react";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../context/AuthContext";
import { cn, userHasPanelAccess } from "../../lib/utils";
import { NotificationBell } from "../../components/visitors/NotificationBell";
import { VisitorBaseProvider } from "../../components/visitors/VisitorBase";

const BASE = "/patrol/visitors";

const ITEMS = [
  { to: BASE, icon: DoorOpen, key: "dashboard", end: true },
  { to: `${BASE}/access`, icon: UserCheck, key: "access" },
  { to: `${BASE}/scan`, icon: QrCode, key: "scan" },
  { to: `${BASE}/preauths`, icon: CalendarCheck, key: "preauths" },
  { to: `${BASE}/registry`, icon: IdCard, key: "registry" },
  { to: `${BASE}/history`, icon: History, key: "history" },
  { to: `${BASE}/vehicles`, icon: Car, key: "vehicles" },
  { to: `${BASE}/incidents`, icon: AlertTriangle, key: "incidents" },
];

/** Control de visitas dentro de la interfaz de patrulla del vigilante. */
export default function GuardVisitorsLayout() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/patrol/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  // Quien tiene acceso al panel usa el módulo dentro del panel principal
  if (userHasPanelAccess(user)) return <Navigate to="/visitors" replace />;

  return (
    <VisitorBaseProvider value={BASE}>
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <header className="bg-slate-800 sticky top-0 z-40">
          <div className="px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Button
                variant="ghost"
                size="icon"
                className="text-slate-300 hover:text-white hover:bg-slate-700 flex-shrink-0"
                onClick={() => navigate("/patrol")}
                title={t("visitors.nav.patrol")}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="min-w-0">
                <p className="text-white font-medium truncate">{t("visitors.nav.group")}</p>
                <p className="text-slate-400 text-xs truncate">{user.name}</p>
              </div>
            </div>
            <NotificationBell side="bottom" align="end" className="text-slate-300 hover:text-white hover:bg-slate-700" />
          </div>
          <nav className="flex gap-1 overflow-x-auto px-2 pb-2 scrollbar-none">
            {ITEMS.map(({ to, icon: Icon, key, end }) => (
              <NavLink
                key={key}
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors",
                    isActive ? "bg-orange-500 text-white" : "text-slate-300 hover:bg-slate-700 hover:text-white"
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {t(`visitors.nav.${key}`)}
              </NavLink>
            ))}
          </nav>
        </header>
        <main className="flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </VisitorBaseProvider>
  );
}
